import React from 'react';
import { Box, InputAdornment, TextField } from '@mui/material';
import { Search, X } from 'lucide-react';

import Text from 'components/text';
import type { Scope } from '../types';
import { FONT } from '../constants';

// A&R Mine/All scope toggle + free-text search — shared by the Requests and Matches tabs.
interface FilterRowProps {
  scope: Scope;
  onScopeChange: (scope: Scope) => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
}

const FilterRow: React.FC<FilterRowProps> = ({
  scope,
  onScopeChange,
  searchInput,
  onSearchInputChange,
}) => (
  <Box
    display="flex"
    flexDirection={{ xs: 'column', md: 'row' }}
    alignItems={{ xs: 'stretch', md: 'center' }}
    gap={1.5}
    padding="12px 20px"
    borderBottom="1px solid #e2e8f0"
    bgcolor="#FAFBFC"
    flexWrap="wrap"
  >
    {/* A&R label + Mine/All toggle — kept together on the first line */}
    <Box display="flex" alignItems="center" gap={1.5}>
      <Text
        fontSize={FONT.sm}
        fontWeight={500}
        color="#64748b"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        A&R
      </Text>
      <Box
        display="inline-flex"
        border="1px solid #e2e8f0"
        borderRadius="6px"
        overflow="hidden"
        bgcolor="#fff"
      >
        {(['mine', 'all'] as Scope[]).map((value, idx) => (
          <Box
            key={value}
            component="button"
            onClick={() => onScopeChange(value)}
            padding="6px 16px"
            fontSize={FONT.sm}
            fontWeight={scope === value ? 700 : 500}
            sx={{
              cursor: 'pointer',
              border: 'none',
              borderLeft: idx > 0 ? '1px solid #e2e8f0' : 'none',
              fontFamily: 'inherit',
              textTransform: 'capitalize',
              color: scope === value ? '#0f172a' : '#64748b',
              backgroundColor: scope === value ? '#f1f2f4' : 'transparent',
            }}
          >
            {value}
          </Box>
        ))}
      </Box>
    </Box>

    <Box
      marginLeft={{ xs: 0, md: 'auto' }}
      width={{ xs: '100%', md: 320 }}
      maxWidth={{ xs: 'none', md: '42%' }}
      minWidth={{ xs: 0, md: 200 }}
    >
      <TextField
        fullWidth
        size="small"
        value={searchInput}
        onChange={(e) => onSearchInputChange(e.target.value)}
        placeholder="Search name, location, genre, or skill…"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} color="#94a3b8" />
            </InputAdornment>
          ),
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <Box
                component="button"
                onClick={() => onSearchInputChange('')}
                aria-label="Clear search"
                display="flex"
                sx={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}
              >
                <X size={14} color="#94a3b8" />
              </Box>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  </Box>
);

export default FilterRow;
